"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import { HubSpotForm } from "@/components/ui/hubspot-form";
import { HUBSPOT_FORMS, HUBSPOT_PORTAL_ID, HUBSPOT_REGION } from "@/data/hubspot-forms";

type Item = { title: string; description: string };
type Plan = { title: string; description: string };

export default function MantenimientoPlacasSolaresPage() {
  const t = useTranslations("mantenimientoPVPage");
  const highlights = t.raw("highlights.items") as Item[];
  const mainParagraphs = t.raw("main.paragraphs") as string[];
  const planA = t.raw("plans.planA") as Plan;
  const planB = t.raw("plans.planB") as Plan;

  return (
    <div className="min-h-screen bg-surface-light text-ink dark:bg-surface-dark dark:text-white">
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-brand-navy text-white dark:bg-surface-dark-deep">
        <div className="absolute inset-0">
          <Image
            src="/static/services/mantenimiento/mantenimiento-hero.webp"
            alt={t("hero.imageAlt")}
            fill
            className="object-cover opacity-80"
            priority
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/40 via-brand-navy/60 to-brand-navy dark:from-black/30 dark:via-surface-dark-deep/70 dark:to-surface-dark-deep" />
        </div>
        <div className="relative mx-auto flex max-w-6xl flex-col gap-6 px-4 pb-16 pt-28 text-center md:pb-24 md:pt-32">
          <span className="mx-auto rounded-full bg-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-label text-[#f7f2ec] shadow-lg">
            {t("hero.badge")}
          </span>
          <h1 className="type-h1">{t("hero.title")}</h1>
          <p className="mx-auto max-w-3xl text-lg md:text-xl text-mist">{t("hero.subtitle")}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild className="px-8 py-3 text-lg uppercase">
              <Link href="#contact-form">{t("hero.cta1")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="px-4 section-y">
        <div className="mx-auto max-w-6xl">
          <p className="text-center text-xs font-semibold uppercase tracking-label text-brand-red">
            {t("highlights.eyebrow")}
          </p>
          <h2 className="mt-2 text-center text-brand-navy dark:text-white type-h2">
            {t("highlights.title")}
          </h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {highlights.map((item) => (
              <div
                key={item.title}
                className="surface-card p-6"
              >
                <h3 className="text-lg font-bold text-brand-navy dark:text-white">{item.title}</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Importancia — text + photo */}
      <section className="bg-white px-4 section-y dark:bg-surface-dark">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <div className="space-y-5">
            <p className="text-xs font-semibold uppercase tracking-label text-brand-red">{t("main.eyebrow")}</p>
            <h2 className="text-brand-navy dark:text-white type-h2">{t("main.title")}</h2>
            {mainParagraphs.map((p, i) => (
              <p key={i} className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                {p}
              </p>
            ))}
          </div>
          <div className="media-frame">
            <Image
              src="/static/services/mantenimiento/mantenimiento-seo-secundaria.webp"
              alt={t("main.imageAlt")}
              width={840}
              height={1050}
              className="h-full w-full object-cover"
              sizes="(min-width: 1024px) 42vw, 100vw"
            />
          </div>
        </div>
      </section>

      {/* Planes */}
      <section className="px-4 section-y">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-semibold uppercase tracking-label text-brand-red">
            {t("plans.eyebrow")}
          </p>
          <h2 className="mt-2 text-brand-navy dark:text-white type-h2">
            {t("plans.title")}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{t("plans.subtitle")}</p>

          {/* Geesol plans */}
          <h3 className="mt-10 text-xl font-bold text-brand-navy dark:text-white">{t("plans.geesolTitle")}</h3>
          <div className="mt-4 grid gap-5 sm:grid-cols-2">
            {[planA, planB].map((plan) => (
              <div
                key={plan.title}
                className="rounded-2xl bg-white p-6 shadow-card dark:bg-surface-dark-raised dark:shadow-card-dark"
              >
                <h4 className="text-lg font-bold text-brand-navy dark:text-white">{plan.title}</h4>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{plan.description}</p>
              </div>
            ))}
          </div>

          {/* External plans */}
          <h3 className="mt-10 text-xl font-bold text-brand-navy dark:text-white">{t("plans.externalTitle")}</h3>
          <div className="mt-4 rounded-2xl bg-white p-6 shadow-card dark:bg-surface-dark-raised dark:shadow-card-dark">
            <p className="text-sm text-gray-600 dark:text-gray-300">{t("plans.externalDescription")}</p>
          </div>
        </div>
      </section>

      {/* HubSpot Form */}
      <section className="bg-brand-navy px-4 section-y text-white dark:bg-surface-dark-deep">
        <div className="hubspot-light mx-auto max-w-5xl rounded-card bg-white p-6 sm:p-8 text-ink shadow-card-dark">
          <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-label text-brand-red">{t("form.eyebrow")}</p>
          <h3 className="mt-3 text-xl sm:text-2xl font-bold text-brand-navy dark:text-white">{t("form.title")}</h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{t("form.subtitle")}</p>
          </div>
          <div className="mt-6">
          <HubSpotForm portalId={HUBSPOT_PORTAL_ID} formId={HUBSPOT_FORMS.contact} region={HUBSPOT_REGION} />
          </div>
        </div>
      </section>
    </div>
  );
}
